import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  MenuItem,
  FormControl,
  OutlinedInput,
  Select as MuiSelect,
  Box,
} from '@mui/material';
import axios from 'axios';
import { ModalShell } from '../ui/shell';
import { Btn, Field, Input, Select, Tag, FormGrid, MODAL_SCRIM_Z_INDEX } from '../ui/primitives';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import NoticeDialog from '../ui/NoticeDialog';
import { useAppContext } from '../../appprovider';
import { parseGateBody } from '../services/apiGate';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const FormRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const GridField = styled(Field)`
  margin-bottom: 0;
  min-width: 0;
`;

const TwoColGrid = styled(FormGrid)`
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const MuiSelectWrap = styled(FormControl)`
  width: 100%;
  min-width: 0;
`;

const Req = styled.span`
  color: ${({ theme }) => theme.danger};
  margin-left: 2px;
`;

/** Returns MUI sx overrides so portaled multi-selects match portal theme tokens. */
function useMuiSelectSx() {
  const theme = useTheme();
  return {
    borderRadius: theme.rMd,
    backgroundColor: theme.surface2,
    color: theme.text,
    fontSize: '13.5px',
    minHeight: theme.controlH,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.borderStrong },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.accent,
      borderWidth: 1,
    },
    '&.Mui-focused': {
      backgroundColor: theme.surface,
      boxShadow: `0 0 0 3px ${theme.accentSoft}`,
    },
    '& .MuiSelect-icon': { color: theme.textMuted },
  };
}

/**
 * Create assessment modal — portal ModalShell + primitives; create API payload unchanged.
 */
export default function FormDialog({ open, handleClose, initialCourse, lockCourse = false, onCreated }) {
  const muiSelectSx = useMuiSelectSx();
  const { openUpgradeGate, loadEntitlements } = useAppContext();
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Text',
    examTime: '',
    examCode: '',
    vendor: '',
    totalMarks: '100',
    courseName: '',
    selectedDomains: [],
    selectedSubDomains: [],
  });
  const [courseDetails, setCourseDetails] = useState({
    courseId: '',
    vendor: '',
    domains: [],
  });
  const [courseNames, setCourseNames] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const userId = localStorage.getItem('userId');
  const apiUrl = process.env.REACT_APP_API_URL;

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  useEffect(() => {
    async function fetchCourseNames() {
      try {
        const response = await fetch(`${apiUrl}/api/course/coursedomain/${userId}`, {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            'Access-Control-Allow-Origin': '*',
            Authorization: getBasicAuthHeader(),
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch course names');
        }
        const responseData = await response.json();
        setCourseNames(responseData);
      } catch (error) {
        // legacy: course fetch errors are ignored
      }
    }
    fetchCourseNames();
  }, [apiUrl, userId]);

  const applyCourse = (selectedCourse) => {
    if (selectedCourse) {
      setCourseDetails({
        courseId: selectedCourse.courseId,
        vendor: selectedCourse.vendor,
        domains: selectedCourse.domains || [],
      });
      setFormData((current) => ({
        ...current,
        courseName: selectedCourse.courseName,
        selectedDomains: [],
        selectedSubDomains: [],
        vendor: selectedCourse.vendor,
      }));
    }
  };

  useEffect(() => {
    if (!open || !initialCourse) return;
    // Prefer the richer entry from the course list, but fall back to the course
    // passed in (it already carries domains/subdomains) so a locked course still
    // populates even if the course-list fetch is empty or hasn't returned yet.
    const match = courseNames.find((course) =>
      String(course.courseId) === String(initialCourse.courseId || initialCourse.id)
      || course.courseName === initialCourse.courseName,
    );
    applyCourse(match || initialCourse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCourse, courseNames]);

  const handleCourseChange = (e) => {
    const selectedCourseName = e.target.value;
    const selectedCourse = courseNames.find((course) => course.courseName === selectedCourseName);
    applyCourse(selectedCourse);
  };

  const handleDomainSelectChange = (event) => {
    const selectedDomainIds = event.target.value || [];
    const selectedDomains = courseDetails.domains.filter((domain) => selectedDomainIds.includes(domain.domainId));
    setFormData({
      ...formData,
      selectedDomains,
      selectedSubDomains: selectedDomains.flatMap((domain) => domain.subDomain),
    });
  };

  const handleSubDomainSelectChange = (event) => {
    const selectedSubDomainIds = event.target.value || [];
    const selectedSubDomains = formData.selectedDomains.flatMap((domain) =>
      domain.subDomain.filter((subDomain) => selectedSubDomainIds.includes(subDomain.subDomainId)),
    );
    setFormData({
      ...formData,
      selectedSubDomains,
    });
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleFormSubmit = async () => {
    if (!guardAction('action.assessment.create.save')) return;
    const { name, examTime, examCode, vendor, totalMarks, type, courseName, selectedDomains } = formData;

    if (!name || !examTime || !examCode || !vendor || !totalMarks || !type || !courseName) {
      setAlertMessage('Please fill all mandatory fields before submitting.');
      return;
    }

    const filteredDomains = selectedDomains.map((domain) => ({
      ...domain,
      subDomain: domain.subDomain.filter((subDomain) =>
        formData.selectedSubDomains.some((selectedSub) => selectedSub.subDomainId === subDomain.subDomainId),
      ),
    }));

    const cleanDomains = (domains) => domains.map(({ courseId, ...rest }) => ({
      ...rest,
      subDomain: rest.subDomain.map(({ domainId, ...subRest }) => subRest),
    }));

    const finalDomain = cleanDomains(filteredDomains);

    setLoading(true);
    const bodyques = {
      name,
      type,
      vendor,
      examCode,
      examTime,
      totalMarks,
      userId,
      courseId: courseDetails.courseId,
      domains: finalDomain,
    };
    try {
      const response = await axios.post(`${apiUrl}/api/assessment/create`, bodyques, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      setLoading(false);
      handleClose();
      if (userId) loadEntitlements(userId);
      if (onCreated) onCreated(response.data);
      else window.location.reload();
    } catch (error) {
      setLoading(false);
      if (error.response?.status === 402) {
        openUpgradeGate(parseGateBody(error.response.data));
        return;
      }
    }
  };

  if (!open) return null;

  return (
    <>
      <NoticeDialog
        open={Boolean(alertMessage)}
        title="Notice"
        message={alertMessage}
        onClose={() => setAlertMessage('')}
      />
      <ModalShell
        title="Create assessment"
        onClose={handleClose}
        max="720px"
        foot={(
          <>
            <Btn type="button" $variant="ghost" onClick={handleClose} disabled={loading}>
              Cancel
            </Btn>
            <Btn type="button" $variant="subtle" onClick={handleFormSubmit} disabled={loading}>
              {loading ? <ButtonSpinnerSlot size={16} /> : 'Create'}
            </Btn>
          </>
        )}
      >
        <FormRoot>
          <GridField>
            <label htmlFor="name">
              Exam name
              <Req>*</Req>
            </label>
            <Input
              id="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter exam name"
            />
          </GridField>

          <TwoColGrid>
            <GridField>
              <label htmlFor="examTime">
                Exam time (minutes)
                <Req>*</Req>
              </label>
              <Input
                id="examTime"
                value={formData.examTime}
                onChange={handleChange}
                placeholder="Minutes"
              />
            </GridField>
            <GridField>
              <label htmlFor="examCode">
                Exam code
                <Req>*</Req>
              </label>
              <Input
                id="examCode"
                value={formData.examCode}
                onChange={handleChange}
                placeholder="Unique code"
              />
            </GridField>
            <GridField>
              <label htmlFor="courseName">
                Course name
                <Req>*</Req>
              </label>
              <Select
                id="courseName"
                value={formData.courseName}
                onChange={handleCourseChange}
                disabled={lockCourse}
              >
                <option value="">Select a course</option>
                {formData.courseName
                  && !courseNames.some((course) => course.courseName === formData.courseName) && (
                    <option value={formData.courseName}>{formData.courseName}</option>
                )}
                {courseNames.map((course) => (
                  <option key={course.courseId} value={course.courseName}>
                    {course.courseName}
                  </option>
                ))}
              </Select>
            </GridField>
            <GridField>
              <label htmlFor="domain-select">Domains</label>
              <MuiSelectWrap variant="outlined">
                <MuiSelect
                  labelId="domain-select-label"
                  id="domain-select"
                  multiple
                  displayEmpty
                  value={formData.selectedDomains.map((domain) => domain.domainId)}
                  onChange={handleDomainSelectChange}
                  MenuProps={{ sx: { zIndex: MODAL_SCRIM_Z_INDEX + 10 } }}
                  input={<OutlinedInput id="select-multiple-chip" sx={muiSelectSx} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {selected.map((domainId) => (
                        <Tag key={domainId} $tone="accent">
                          {courseDetails.domains.find((domain) => domain.domainId === domainId)?.name}
                        </Tag>
                      ))}
                    </Box>
                  )}
                  sx={muiSelectSx}
                >
                  {courseDetails.domains.map((domain) => (
                    <MenuItem key={domain.domainId} value={domain.domainId}>
                      {domain.name}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </MuiSelectWrap>
            </GridField>
            <GridField style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="subdomain-select">Subdomains</label>
              <MuiSelectWrap variant="outlined">
                <MuiSelect
                  labelId="subdomain-select-label"
                  id="subdomain-select"
                  multiple
                  displayEmpty
                  value={formData.selectedSubDomains.map((subDomain) => subDomain.subDomainId)}
                  onChange={handleSubDomainSelectChange}
                  MenuProps={{ sx: { zIndex: MODAL_SCRIM_Z_INDEX + 10 } }}
                  input={<OutlinedInput id="select-multiple-subdomain" sx={muiSelectSx} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {selected.map((subDomainId) => (
                        <Tag key={subDomainId} $tone="accent">
                          {formData.selectedSubDomains.find(
                            (subDomain) => subDomain.subDomainId === subDomainId,
                          )?.name}
                        </Tag>
                      ))}
                    </Box>
                  )}
                  sx={muiSelectSx}
                >
                  {formData.selectedDomains
                    .flatMap((domain) => domain.subDomain)
                    .map((subDomain) => (
                      <MenuItem key={subDomain.subDomainId} value={subDomain.subDomainId}>
                        {subDomain.name}
                      </MenuItem>
                    ))}
                </MuiSelect>
              </MuiSelectWrap>
            </GridField>
          </TwoColGrid>
        </FormRoot>
      </ModalShell>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
